import {FieldMapRasterLayer} from "../../types/fieldMapLayer";
import {FieldResultLayersProps, FieldResultRasterLayerProps} from "../../types/domain/fieldResult";
import {RasterLayerType} from "../../types/domain/rasterLayerType";
import {RasterLegendScope} from "../../types/mapSelectedLayers";

export const getEffectiveLegendScope = ({
    canUseFieldScope,
    legendScope,
}: {
    canUseFieldScope: boolean;
    legendScope: RasterLegendScope;
}): RasterLegendScope => {
    return canUseFieldScope ? legendScope : "area";
};

export const hasDetailedRasterProps = ({
    activeFieldId,
    chosenLayer,
    layerResults,
}: {
    activeFieldId: string | null;
    chosenLayer: RasterLayerType;
    layerResults: Record<string, FieldResultLayersProps> | null;
}) => {
    if (!activeFieldId) {
        return false;
    }

    return Boolean(layerResults?.[activeFieldId]?.detailedRasterProps?.[chosenLayer]);
};

interface RasterSourceDetails {
    props: FieldResultRasterLayerProps;
    isDetailed: boolean;
}

export const getRasterSourceDetails = ({
    activeFieldId,
    chosenLayer,
    fieldId,
    legendScope,
    resultProps,
}: {
    activeFieldId: string | null;
    chosenLayer: RasterLayerType;
    fieldId: string;
    legendScope: RasterLegendScope;
    resultProps: FieldResultLayersProps;
}): RasterSourceDetails | null => {
    if (legendScope === "field" && activeFieldId === fieldId) {
        const detailedProps = resultProps.detailedRasterProps?.[chosenLayer];
        if (detailedProps) {
            return {
                props: detailedProps,
                isDetailed: true,
            };
        }
    }

    const defaultProps = resultProps.rasterProps?.[chosenLayer];
    if (!defaultProps) {
        return null;
    }

    return {
        props: defaultProps,
        isDetailed: false,
    };
};

export const shouldRenderRasterLayer = ({
    activeFieldId,
    fieldId,
    legendScope,
}: {
    activeFieldId: string | null;
    fieldId: string;
    legendScope: RasterLegendScope;
}) => {
    if (legendScope !== "field" || !activeFieldId) {
        return true;
    }

    return activeFieldId === fieldId;
};

export const getRasterImageFileName = ({
    chosenLayer,
    imageId,
    isDetailed,
}: {
    chosenLayer: RasterLayerType;
    imageId: string;
    isDetailed: boolean;
}) => {
    const baseFileName = imageId.startsWith(chosenLayer) ? imageId : `${chosenLayer}_${imageId}`;
    return `${baseFileName}${isDetailed ? "_detailed" : ""}.png`;
};

export const getLegendRange = ({
    activeFieldId,
    legendScope,
    rasterLayers,
}: {
    activeFieldId: string | null;
    legendScope: RasterLegendScope;
    rasterLayers: FieldMapRasterLayer[];
}) => {
    const relevantLayers =
        legendScope === "field" && activeFieldId
            ? rasterLayers.filter((layer) => layer.fieldId === activeFieldId)
            : rasterLayers;

    if (relevantLayers.length === 0) {
        return null;
    }

    const min = Math.min(...relevantLayers.map((layer) => layer.min ?? 0));
    const max = Math.max(...relevantLayers.map((layer) => layer.max ?? 0));

    return {min, max};
};
